import { activeEnv } from '../../build-scripts/environment';
import { HorizontalFocus } from '../enums/image';

import { getBynderCName, getBynderDomain, getCloudinaryCName } from './config-helper';
import { trimPath } from './path-matching-helper';
import { isNullOrBlank } from './string-helper';

export enum HostedImageProvider {
  CLOUDINARY = 'Cloudinary',
  BYNDER = 'Bynder',
}

export const CLOUDINARY_CLOUD_NAME = 'softwire-david-lloyd';

export const mobileBreakpointInPixels = 480;
const defaultSrcSetBreakpoints = [mobileBreakpointInPixels, 640, 800, 1024, 1400, 1600];

type ImageSrcOptions = {
  widthLimit?: number;
  crop?: Queries.ImageCrop;
  trimTransparentBorder?: boolean;
};

export const getSrc = (imageUrlOrPath: string, options: ImageSrcOptions = {}) => {
  const { widthLimit, crop, trimTransparentBorder } = options;
  return getSizedImageUrl(imageUrlOrPath, widthLimit, crop, trimTransparentBorder);
};

export const getSizedImageUrl = (
  imageUrlOrPath: string,
  widthLimit?: number,
  crop?: Queries.ImageCrop,
  trimTransparentBorder?: boolean
) => {
  const parsedImagePath = getDecodedAssetDetailsOrNullIfNotFound(imageUrlOrPath);

  if (!parsedImagePath?.path) {
    return imageUrlOrPath;
  }

  if (parsedImagePath.provider === HostedImageProvider.CLOUDINARY) {
    return getCloudinarySizedImageUrl(
      parsedImagePath.path,
      widthLimit,
      crop,
      trimTransparentBorder
    );
  } else {
    return getBynderSizedImageUrl(parsedImagePath.path, widthLimit, crop, trimTransparentBorder);
  }
};

const getCloudinarySizedImageUrl = (
  cloudinaryPath: string,
  widthLimit?: number,
  crop?: Queries.ImageCrop,
  trimTransparentBorder?: boolean
) => {
  const cloudinaryTrimTransformation = trimTransparentBorder ? '/e_trim' : '';
  const cloudinaryCropTransformation = !!crop
    ? `/${encodeURIComponent(`c_crop,g_xy_center,x_${crop.x},y_${crop.y},w_${crop.width},h_${crop.height}`)}`
    : '';
  const cloudinaryWidthLimitTransformation = !!widthLimit
    ? `/${encodeURIComponent(`c_limit,w_${widthLimit}`)}`
    : '';

  return (
    `${getCloudinaryCName()}/${CLOUDINARY_CLOUD_NAME}` +
    '/image/upload' +
    '/f_auto' +
    cloudinaryTrimTransformation +
    cloudinaryCropTransformation +
    cloudinaryWidthLimitTransformation +
    '/v1' +
    `/${encodeURI(cloudinaryPath)}`
  );
};

const getBynderSizedImageUrl = (
  bynderPath: string,
  widthLimit?: number,
  crop?: Queries.ImageCrop,
  trimTransparentBorder?: boolean
) => {
  const queryParams = new URLSearchParams();

  if (trimTransparentBorder) {
    queryParams.append('io', 'transform:trim');
  }

  if (crop?.widthPx && crop?.heightPx) {
    queryParams.append('io', `transform:crop,width:${crop.widthPx},height:${crop.heightPx}`);
    queryParams.append('focuspoint', `${crop.x.toFixed(5)},${crop.y.toFixed(5)}`);
  }

  if (widthLimit) {
    queryParams.append('io', `transform:scale,width:${widthLimit}`);
  }

  queryParams.append('format', 'webp');

  let baseUrl = `${getBynderCName()}/${bynderPath}`;

  if (!bynderPath.startsWith('transform/')) {
    baseUrl += '/transform/';
  }

  return baseUrl + `?${queryParams.toString()}`;
};

const VIDEO_EXTENSION_REGEX = /^(.*)(\.[a-zA-Z0-9]+)$/;

export const getCloudinaryAdaptiveBitrateStreamingVideoUrl = (
  videoUrlOrPath: string
): string | null => {
  const urlWithoutExtension = videoUrlOrPath.match(VIDEO_EXTENSION_REGEX)?.[1] ?? videoUrlOrPath;
  const fullUrlWithExtension = `${urlWithoutExtension}.m3u8`;

  const parsedImagePath = getDecodedAssetDetailsOrNullIfNotFound(fullUrlWithExtension);

  const url =
    parsedImagePath?.provider === HostedImageProvider.CLOUDINARY
      ? `${getCloudinaryCName()}/${CLOUDINARY_CLOUD_NAME}/video/upload/sp_auto/v1/${parsedImagePath.path}`
      : fullUrlWithExtension;

  return encodeURI(url);
};

export const getBynderLocallyHostedPath = (bynderUrl: string): string => {
  const bynderAssetPath = trimPath(bynderUrl.split(getBynderDomain())[1]);
  return `/static/imagelibrary/${bynderAssetPath}`;
};

export const getBynderAdaptiveBitrateStreamingVideoUrl = (
  videoUrlOrPath: string
): string | null => {
  if (!videoUrlOrPath.startsWith(getBynderDomain())) {
    return videoUrlOrPath;
  }
  return activeEnv === 'development' ? videoUrlOrPath : getBynderLocallyHostedPath(videoUrlOrPath);
};

export const getImageForClubFinder = (imageUrlOrPath: string) => {
  const parsedImageDetails = getDecodedAssetDetailsOrNullIfNotFound(imageUrlOrPath);

  if (!parsedImageDetails || isNullOrBlank(parsedImageDetails.path)) {
    return encodeURI(imageUrlOrPath);
  }

  if (parsedImageDetails.provider === HostedImageProvider.CLOUDINARY) {
    const url = `${getCloudinaryCName()}/${CLOUDINARY_CLOUD_NAME}/image/upload/w_160,h_160,c_fill/v1/${parsedImageDetails.path}`;
    return encodeURI(url);
  }

  const untransformedUrl = getBynderSizedImageUrl(parsedImageDetails.path, undefined, undefined);
  const result = new URL(untransformedUrl);
  result.searchParams.append('io', 'transform:fill,width:160,height:160');
  return result.toString();
};

// Expected full URL pattern = https://<domain>/<first part of slug>/v<some digits>/<file path (including subdirectories)>
// e.g. https://res.cloudinary.com/softwire-david-lloyd/image/upload/v1638362073/recipes/envelope.jpg
const cloudinaryImageUrlRegex = /^https:\/\/\S+\/v\d+\/(\S+)$/;

export const getDecodedAssetDetailsOrNullIfNotFound = (
  imageUrlOrPath: string
): {
  provider: HostedImageProvider;
  path: string;
} | null => {
  if (isImagePath(imageUrlOrPath)) {
    return {
      provider: HostedImageProvider.CLOUDINARY,
      path: decodeURI(imageUrlOrPath),
    };
  }

  if (imageUrlOrPath.startsWith(getBynderDomain())) {
    const bynderAssetPath = imageUrlOrPath.split(getBynderDomain())[1];

    if (!bynderAssetPath.startsWith('/match/') && !bynderAssetPath.startsWith('/transform/')) {
      return null;
    }

    return !isNullOrBlank(bynderAssetPath)
      ? {
          provider: HostedImageProvider.BYNDER,
          path: trimPath(decodeURI(bynderAssetPath)),
        }
      : null;
  }

  const cloudinaryUrlMatch = cloudinaryImageUrlRegex.exec(imageUrlOrPath);

  if (cloudinaryUrlMatch === null || isNullOrBlank(cloudinaryUrlMatch[1])) {
    return null;
  }

  return {
    provider: HostedImageProvider.CLOUDINARY,
    path: decodeURI(cloudinaryUrlMatch[1]),
  };
};

const isImagePath = (imageUrlOrPath: string) => !imageUrlOrPath.startsWith('https');

export const getSrcSet = (imagePath: string, options: ImageSrcOptions = {}) => {
  const { widthLimit, crop, trimTransparentBorder } = options;
  // This is specifying the approximate size for the image for srcSet, but will be default image option even if the screen/image is bigger
  const maxPageWidthInPixels = 1920;
  const originalImageUrl = getSrc(imagePath, options);

  const breakpoints =
    widthLimit === undefined
      ? defaultSrcSetBreakpoints
      : defaultSrcSetBreakpoints.filter((bp) => bp < widthLimit).concat([widthLimit]);

  const srcSetEntries = [
    ...breakpoints.map(
      (w) => `${getSizedImageUrl(imagePath, w, crop, trimTransparentBorder)} ${w}w`
    ),
  ];

  if (widthLimit === undefined) {
    srcSetEntries.push(`${originalImageUrl} ${maxPageWidthInPixels}w`);
  }

  return srcSetEntries.join(', ');
};

export const getObjectPosition = (imageDetails: Queries.ImageDetails) => {
  switch (imageDetails.horizontalFocus) {
    case HorizontalFocus.START:
      return '0% 50%';
    case HorizontalFocus.LEFT:
      return '25% 50%';
    case HorizontalFocus.RIGHT:
      return '75% 50%';
    case HorizontalFocus.END:
      return '100% 50%';
    default:
      return '50% 55%';
  }
};
